// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse } from "@/types";
import { buildBody, buildUrl } from "./helpers";
import type { TyFetchOptions, TyFetchResult } from "./types";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The only way this app talks to the API. It attaches `Authorization` and `x-language` itself, so no
 * screen has to remember them and no screen can forget one.
 *
 * There is no `x-organization-id` here. An admin is above every tenant and never has one active — the
 * organization a request is about travels in the path, which is why nothing in this app can leak one
 * company's data into another's screen by forgetting a header.
 *
 * `init: false` defers the call until `invoke()` — which is what every form and row action uses.
 */
export default function useFetch<T = unknown>(path: string, baseOptions: TyFetchOptions = {}, init: boolean = true): TyFetchResult<T> {
    const token = useAuth((state) => state.token);
    const logout = useAuth((state) => state.logout);
    const lang = useLang((state) => state.lang);
    const t = useTranslation();

    const [pendingOptions, setPendingOptions] = useState<TyFetchOptions | null>(null);
    const [work, setWork] = useState(init ? 1 : 0);
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(init);
    const [error, setError] = useState<string | null>(null);
    const [errorData, setErrorData] = useState<unknown>(null);
    const [status, setStatus] = useState<number | null>(null);
    const [succeededAt, setSucceededAt] = useState<number | null>(null);

    useEffect(() => {
        if (work === 0) return;
        const controller = new AbortController();
        const options = { ...baseOptions, ...pendingOptions };

        async function fetching() {
            setLoading(true);
            setError(null);
            setErrorData(null);

            const headers: Record<string, string> = { "x-language": lang ?? "ar" };
            if (token) headers["Authorization"] = `Bearer ${token}`;

            const requestInit: RequestInit = { method: options.method ?? "GET", headers, signal: options.signal ?? controller.signal };
            if (options.data) {
                const { body, contentType } = buildBody(options.data);
                requestInit.body = body;
                if (contentType) headers["Content-Type"] = contentType;
            }

            try {
                const response = await fetch(buildUrl(options.path ?? path, options), requestInit);
                const payload = (await response.json()) as TyApiResponse<T>;

                setStatus(response.status);
                // A revoked or invalid token is dead everywhere, so the session goes with it.
                if (response.status === 401) logout();

                if (!response.ok || !payload.success) {
                    setError(payload.message || t.common.somethingWrong);
                    // `data` is left alone: a refusal must not overwrite what the screen is showing.
                    setErrorData(payload.data ?? null);
                } else {
                    setData(payload.data ?? null);
                    setSucceededAt(Date.now());
                }
            } catch (err) {
                if ((err as Error)?.name === "AbortError") return;
                setError(t.common.somethingWrong);
            } finally {
                setLoading(false);
            }
        }

        fetching();
        return () => controller.abort();
        /**
         * Keyed on `work` alone. A deferred call (`init: false`) sits at `work === 0` and stays idle
         * until a screen asks for it; everything else re-runs by bumping the counter through `invoke`.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [work]);

    const invoke = (overrideOptions?: TyFetchOptions) => {
        setPendingOptions(overrideOptions ?? null);
        setWork((previous) => previous + 1);
    };

    return { data, loading, error, errorData, status, succeededAt, invoke };
}
